import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Layers, LayoutGrid, Droplets, Zap } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { CallButton, WhatsAppButton } from "@/components/ContactButtons";
import Marquee from "@/components/Marquee";
import BeforeAfter from "@/components/BeforeAfter";
import MapSection from "@/components/MapSection";
import CtaBanner from "@/components/CtaBanner";
import { IMAGES, SERVICES, TRADES, PROCESS_STEPS } from "@/constants/site";

const HERO_LINES = ["Trasformiamo", "la tua casa,", "con cura artigiana."];

const STATS = [
  { value: "250+", label: "Progetti completati" },
  { value: "15", label: "Anni di esperienza" },
  { value: "10", label: "Anni di garanzia" },
];

const TRADE_ICONS = {
  cartongessista: Layers,
  piastrellista: LayoutGrid,
  idraulico: Droplets,
  elettricista: Zap,
};

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.35]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#161514]"
    >
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Bagno di lusso ristrutturato a Osimo"
          className="h-[115%] w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#161514] via-[#161514]/55 to-[#161514]/25" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-36 sm:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <Overline light>Ristrutturazioni chiavi in mano — Osimo e tutte le Marche</Overline>
        </motion.div>

        <h1 className="mt-6 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-[#FAF8F5] sm:text-5xl lg:text-6xl">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "italic text-[#C85A32]" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#FAF8F5]/70 md:text-lg"
        >
          Bagni, cucine e ristrutturazioni complete eseguite dalle nostre
          maestranze: cartongessisti, piastrellisti, idraulici ed elettricisti.
          Un solo interlocutore, zero pensieri.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row"
        >
          <CallButton testid="hero-call-button" light />
          <WhatsAppButton testid="hero-whatsapp-button" light />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-14 grid grid-cols-3 gap-6 border-t border-[#FAF8F5]/15 pt-7"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <p className="font-serif text-3xl font-semibold text-[#FAF8F5] sm:text-4xl">
                {s.value}
              </p>
              <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#B88E4C]">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ServicesBento() {
  return (
    <section data-testid="services-bento" className="bg-[#F7F5F0] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>I Nostri Servizi</Overline>
            <h2 className="mt-4 max-w-lg font-serif text-2xl font-semibold tracking-tight text-[#1A1918] sm:text-3xl lg:text-4xl">
              Tre specialità, un unico standard: l'eccellenza.
            </h2>
          </div>
          <Link
            to="/servizi"
            data-testid="bento-all-services-link"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#C85A32]"
          >
            Tutti i servizi
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>

        <div className="mt-12 grid gap-4 lg:h-[560px] lg:grid-cols-6 lg:grid-rows-2">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.id}
              delay={0.1 * i}
              className={
                i === 0 ? "lg:col-span-4 lg:row-span-2" : "lg:col-span-2 lg:row-span-1"
              }
            >
              <Link
                to="/servizi"
                data-testid={`service-card-${s.id}`}
                className="group relative block h-64 w-full overflow-hidden rounded-2xl sm:h-72 lg:h-full"
              >
                <img
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#161514]/90 via-[#161514]/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 sm:p-7">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#B88E4C]">
                      0{i + 1}
                    </p>
                    <h3 className="mt-1.5 font-serif text-xl font-semibold text-[#FAF8F5] sm:text-2xl">
                      {s.title}
                    </h3>
                    {i === 0 && (
                      <p className="mt-2 hidden max-w-md text-sm leading-relaxed text-[#FAF8F5]/70 sm:block">
                        {s.short}
                      </p>
                    )}
                  </div>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#FAF8F5]/30 text-[#FAF8F5] transition-all duration-300 group-hover:border-[#C85A32] group-hover:bg-[#C85A32]">
                    <ArrowUpRight size={18} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Trades() {
  return (
    <section data-testid="trades-section" className="bg-[#161514] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Overline light>Maestranze e Specializzazioni</Overline>
          <h2 className="mt-4 max-w-xl font-serif text-2xl font-semibold tracking-tight text-[#FAF8F5] sm:text-3xl lg:text-4xl">
            Quattro mestieri, una sola squadra interna.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#FAF8F5]/60 md:text-lg">
            Nessun subappalto: ogni lavorazione è eseguita dai nostri artigiani
            di fiducia, coordinati da un unico capocantiere.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TRADES.map((t, i) => {
            const Icon = TRADE_ICONS[t.id];
            return (
              <Reveal key={t.id} delay={0.08 * i}>
                <article
                  data-testid={`trade-card-${t.id}`}
                  className="group h-full overflow-hidden rounded-2xl border border-[#FAF8F5]/10 bg-[#22201E] transition-colors duration-300 hover:border-[#C85A32]/50"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute bottom-3 left-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#C85A32] text-[#FAF8F5]">
                      <Icon size={17} />
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl font-semibold text-[#FAF8F5]">{t.name}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-[#FAF8F5]/60">{t.desc}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSection() {
  return (
    <section data-testid="before-after-section" className="bg-[#F7F5F0] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Prima / Dopo</Overline>
            <h2 className="mt-4 max-w-lg font-serif text-2xl font-semibold tracking-tight text-[#1A1918] sm:text-3xl lg:text-4xl">
              Trascina il cursore e guarda la differenza.
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#6E6A64]">
            Una cucina a Osimo: demolizione, nuovi impianti e
            finiture su misura in 6 settimane.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <BeforeAfter before={IMAGES.kitchenBefore} after={IMAGES.kitchen} />
        </Reveal>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section data-testid="process-section" className="bg-[#EFECE6] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Overline>Come Lavoriamo</Overline>
          <h2 className="mt-4 max-w-xl font-serif text-2xl font-semibold tracking-tight text-[#1A1918] sm:text-3xl lg:text-4xl">
            Dal primo sopralluogo alle chiavi in mano.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((p, i) => (
            <Reveal key={p.n} delay={0.1 * i}>
              <div data-testid={`process-step-${p.n}`} className="border-t-2 border-[#C85A32] pt-6">
                <p className="font-mono text-sm text-[#B88E4C]">{p.n}</p>
                <h3 className="mt-3 text-xl font-semibold text-[#1A1918] sm:text-2xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#6E6A64]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <ServicesBento />
      <Trades />
      <BeforeAfterSection />
      <Process />
      <MapSection />
      <CtaBanner />
    </main>
  );
}
